import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  UserCircleIcon, 
  Cog6ToothIcon, 
  ArrowRightOnRectangleIcon,
  HomeIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline';

const Header = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [myAccountOpen, setMyAccountOpen] = useState(false);
  const [desktopAccountDropdown, setDesktopAccountDropdown] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setMyAccountOpen(false); // Reset My Account when closing menu
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMyAccountOpen(false);
  };

  const toggleMyAccount = () => {
    setMyAccountOpen(!myAccountOpen);
  };

  const handleMyAccountNavigation = (path) => {
    navigate(path);
    closeMobileMenu();
  };

  const toggleDesktopAccountDropdown = () => {
    setDesktopAccountDropdown(!desktopAccountDropdown);
  };

  const handleDesktopNavigation = (path) => {
    navigate(path);
    setDesktopAccountDropdown(false);
  };

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopAccountDropdown && !event.target.closest('.account-dropdown-container')) {
        setDesktopAccountDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [desktopAccountDropdown]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Simple threshold - becomes sticky after scrolling past 150px
      if (scrollTop > 150) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Top Government Bar */}
      <div className={`bg-[#f0f0f0] border-b border-gray-200 transition-all duration-300 ${isSticky ? 'opacity-0 -translate-y-full' : 'opacity-100 translate-y-0'}`}>
        <div className="max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-sm">
            <div className="flex flex-col lg:flex-row lg:items-center text-[0.8rem]">
              <div className="flex items-center">
                <div className="w-5 h-3 mr-2 flex items-center justify-center text-white text-xs">
                  <img src='images/us.png' />
                </div>
                <span className="text-gray text-[0.8rem]">An official website of the United States government</span>
              </div>
              <button className="text-left lg:ml-2 text-[#0057a3] underline hover:text-blue-800 flex items-center mt-1 lg:mt-0">
                Here's how you know
                <ChevronDownIcon className="h-3 w-3 ml-1" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-[#f9fafb] pb-4 shadow-sm border-b border-gray-200">
        <div className="max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col">
            {/* Google language translator */}
            <div className={`flex items-center justify-end lg:justify-end justify-center space-x-6 py-3 order-2 lg:order-1 border-t border-b border-[#8c91a0] md:border-t-0 md:border-b-0 transition-all duration-300 ${isSticky ? 'opacity-0 -translate-y-full' : 'opacity-100 translate-y-0'}`}>
              <span id="google_translate_element" className="google-translate-widget"></span>
              
              <div className="h-4 w-px bg-gray-300"></div>
              
              <button className="flex items-center text-[#0057a3] hover:text-blue-800 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 mr-2">
                  <path fillRule="evenodd" d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742 19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742ZM12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clipRule="evenodd" />
                </svg>
                Find a Federal Court
              </button>
            </div>

            {/* Main Navigation Bar */}
            <div 
              id="main-navigation-bar"
              className={`flex justify-between items-center py-2 order-1 lg:order-2 transition-all duration-300 ${
                isSticky 
                  ? 'fixed top-0 left-0 right-0 z-50 bg-[#f9fafb] shadow-md' 
                  : 'relative'
              }`}
              style={isSticky ? { width: '100%' } : {}}
            >
              <div className={`flex justify-between items-center w-full ${isSticky ? 'max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8' : ''}`}>
                {/* Logo */}
                <Link to="/" className="flex-shrink-0">
                  <img 
                    src='images/logo.png' 
                    style={isSticky ? {height: 60, width: 120} : {height: 85, width: 170}} 
                    className="transition-all duration-300"
                  />
                </Link>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-8 flex-1 justify-center">
                <div className="relative group">
                  <Link to="/about-federal-courts" className="hover:text-[#0057a3] transition-colors flex items-center text-[16px] text-[#002045]">
                    About Federal Courts
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </Link>
                </div>
                
                <div className="relative group">
                  <Link to="/administration-policies" className="hover:text-[#0057a3] transition-colors flex items-center text-[16px] text-[#002045]">
                    Administration & Policies
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </Link>
                </div>

                <div className="relative group">
                  <Link to="/data-news" className="hover:text-[#0057a3] transition-colors flex items-center text-[16px] text-[#002045]">
                    Data & News
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </Link>
                </div>

                <div className="relative group">
                  <Link to="/forms-rules" className="hover:text-[#0057a3] transition-colors flex items-center text-[16px] text-[#002045]">
                    Forms & Rules
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </Link>
                </div>
                <div className="relative group">
                  <Link to="/case-lookup" className="hover:text-[#0057a3] transition-colors flex items-center text-[16px] text-[#002045]">
                    Case Lookup
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </Link>
                </div>
                <div className="relative group">
                  <Link to="/fintech" className="hover:text-[#0057a3] transition-colors flex items-center text-[16px] text-[#002045]">
                    FDIC
                  </Link>
                </div>

                {/* EDIT: For unauthenticated users, replaced standalone 'Case Lookup' + 'Sign In' with unified 'My Account' dropdown */}
                {/* OLD CODE - commented out:
                {!isAuthenticated && (
                  <div className="relative group">
                    <Link to="/case-lookup" className="hover:text-[#0057a3] transition-colors flex items-center text-[16px] text-[#002045]">
                      Case Lookup
                      <ChevronDownIcon className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                )}
                END OLD CODE */}

                {/* ---- LOGGED IN: untouched ---- */}
                {isAuthenticated ? (
                  <div className="relative account-dropdown-container">
                    <button
                      onClick={toggleDesktopAccountDropdown}
                      className="text-gray-700 hover:text-[#0057a3] transition-colors flex items-center text-sm font-medium"
                    >
                      <UserCircleIcon className="h-5 w-5 mr-1" />
                      My Account
                      <ChevronDownIcon className="ml-1 h-4 w-4" />
                    </button>

                    {desktopAccountDropdown && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <div className="flex items-center space-x-2">
                            <UserCircleIcon className="h-6 w-6 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                              <p className="text-xs text-gray-500">{user?.email}</p>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => handleDesktopNavigation('/dashboard')}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                        >
                          <HomeIcon className="h-5 w-5 text-[#0057a3]" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">Dashboard</div>
                            <div className="text-xs text-gray-500">View your dashboard</div>
                          </div>
                        </button>

                        <button
                          onClick={() => handleDesktopNavigation('/online-payment')}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-[#0057a3]">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
                          </svg>
                          <div>
                            <div className="text-sm font-medium text-gray-900">Online Payment Center</div>
                            <div className="text-xs text-gray-500">Make payments online</div>
                          </div>
                        </button>

                        <div className="border-t border-gray-200 mt-2 pt-2">
                          <button
                            onClick={() => {
                              handleLogout();
                              setDesktopAccountDropdown(false);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                          >
                            <ArrowRightOnRectangleIcon className="h-5 w-5 text-red-600" />
                            <div>
                              <div className="text-sm font-medium text-red-600">Logout</div>
                              <div className="text-xs text-gray-500">Sign out of your account</div>
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ---- NOT LOGGED IN: NEW My Account dropdown replacing standalone Sign In + Case Lookup ---- */
                  /* OLD CODE - commented out:
                  <Link to="/login" className="hover:text-[#0057a3] transition-colors flex items-center text-[16px] text-[#002045]">
                    Sign In
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </Link>
                  END OLD CODE */

                  /* NEW */
                  <div className="relative account-dropdown-container">
                    <button
                      onClick={toggleDesktopAccountDropdown}
                      className="text-[#002045] hover:text-[#0057a3] transition-colors flex items-center text-[16px] font-medium"
                    >
                      <UserCircleIcon className="h-5 w-5 mr-1" />
                      My Account
                      <ChevronDownIcon className="ml-1 h-4 w-4" />
                    </button>

                    {desktopAccountDropdown && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">

                        {/* Account Summary → Case Lookup page */}
                        <button
                          onClick={() => handleDesktopNavigation('/case-lookup')}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                        >
                          <HomeIcon className="h-5 w-5 text-[#0057a3]" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">Account Summary</div>
                            <div className="text-xs text-gray-500">View your account summary</div>
                          </div>
                        </button>

                        {/* Account Summary → Fintech Dashboard page */}
                        <button
                          onClick={() => handleDesktopNavigation('/fintech')}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                        >
                          <HomeIcon className="h-5 w-5 text-[#0057a3]" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">FDIC</div>
                            <div className="text-xs text-gray-500">Manage your FDIC account</div>
                          </div>
                        </button>

                        {/* Online Payment Center */}
                        <button
                          onClick={() => handleDesktopNavigation('/online-payment')}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-[#0057a3]">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
                          </svg>
                          <div>
                            <div className="text-sm font-medium text-gray-900">Online Payment Center</div>
                            <div className="text-xs text-gray-500">Make payments online</div>
                          </div>
                        </button>

                        {/* Login */}
                        <div className="border-t border-gray-200 mt-2 pt-2">
                          <button
                            onClick={() => handleDesktopNavigation('/login')}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                          >
                            <ArrowRightOnRectangleIcon className="h-5 w-5 text-[#0057a3]" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">Login</div>
                              <div className="text-xs text-gray-500">Sign in to your account</div>
                            </div>
                          </button>
                        </div>

                      </div>
                    )}
                  </div>
                  /* END NEW */
                )}

              </nav>

                {/* Search Button */}
                <div className="hidden lg:block flex-shrink-0">
                  <Link to="/search" className="bg-[#0057a3] text-white px-6 py-2 rounded-full hover:bg-[#01417f] transition-colors flex items-center text-[16px] font-medium">
                    Search
                    <MagnifyingGlassIcon className="h-4 w-4 ml-2 text-[16px]" />
                  </Link>
                </div>

                {/* Mobile Menu Button */}
                <div className="lg:hidden flex items-center space-x-4">
                  <button
                    onClick={toggleMobileMenu}
                    className="text-gray-700 hover:text-[#0057a3] flex items-center"
                  >
                    <span className="mr-2 text-[#0057a3] text-[0.8rem]">Menu</span>
                    <img src='images/menu.png' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer when sticky to prevent content jump */}
      {isSticky && <div style={{ height: '68px' }} />}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-white lg:hidden">
          <div className="flex flex-col h-full">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
              <h2 className="text-xl font-semibold text-gray-900">Main Menu</h2>
              <button onClick={toggleMobileMenu} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Mobile Menu Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 relative">
              <div className={`transition-transform duration-300 ${myAccountOpen ? '-translate-x-full' : 'translate-x-0'}`}>

                {/* Search */}
                <div className="p-4 bg-white">
                  <h3 className="text-gray-900 font-bold text-base mb-3">Search USCourts.gov</h3>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder=""
                      style={{ borderRadius: '40px' }}
                    />
                    <button className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-[#0057a3] text-white p-2 rounded-full hover:bg-blue-700">
                      <MagnifyingGlassIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="mt-2">
                  <div className="bg-white border-b border-gray-200">
                    <Link to="/about-federal-courts" onClick={closeMobileMenu} className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors block">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-base mb-1">About Federal Courts</div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          The U.S. Courts were created under Article III of the Constitution to administer justice fairly.
                        </div>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
                    </Link>
                  </div>

                 {/* <div className="bg-white border-b border-gray-200">
                    <Link to="/#" onClick={closeMobileMenu} className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors block">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-base mb-1">Court Programs</div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          Learn about federal court programs and services to help provide fair and impartial justice.
                        </div>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
                    </Link>
                  </div>*/}

                  <div className="bg-white border-b border-gray-200">
                    <Link to="/administration-policies" onClick={closeMobileMenu} className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors block">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-base mb-1">Administration & Policies</div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          Administration and governance of the Judicial occurs at both a national and regional level.
                        </div>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
                    </Link>
                  </div>

                  <div className="bg-white border-b border-gray-200">
                    <Link to="/data-news" onClick={closeMobileMenu} className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors block">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-base mb-1">Data & News</div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          This section provides statistical data and news on the business of the federal Judiciary.
                        </div>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
                    </Link>
                  </div>

                  <div className="bg-white border-b border-gray-200">
                    <Link to="/forms-rules" onClick={closeMobileMenu} className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors block">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-base mb-1">Forms & Rules</div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          Link to the national federal rules of practice and procedures, and forms in effect.
                        </div>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
                    </Link>
                  </div>
                  <div className="bg-white border-b border-gray-200">
                    <Link to="/case-lookup" onClick={closeMobileMenu} className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors block">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-base mb-1">Case Lookup</div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          Search and lookup court cases and documents.
                        </div>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
                    </Link>
                  </div>
                  <div className="bg-white border-b border-gray-200">
                    <Link to="/fintech" onClick={closeMobileMenu} className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors block">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-base mb-1">FDIC</div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          Manage your FDIC account and access related services.
                        </div>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
                    </Link>
                  </div>

                  {/* EDIT: For unauthenticated users, replaced standalone 'Case Lookup' + 'Sign In/Sign Up' with unified 'My Account' submenu entry */}
                  {/* OLD CODE - commented out:
                  {!isAuthenticated && (
                    <div className="bg-white border-b border-gray-200">
                      <Link to="/case-lookup" onClick={closeMobileMenu} className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors block">
                        <div className="flex-1">
                          <div className="font-bold text-gray-900 text-base mb-1">Case Lookup</div>
                          <div className="text-sm text-gray-600 leading-relaxed">
                            Search and lookup court cases and documents.
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
                      </Link>
                    </div>
                  )}

                  {!isAuthenticated && (
                    <div className="bg-white border-b border-gray-200">
                      <div className="px-4 py-4">
                        <div className="flex space-x-4">
                          <Link to="/login" onClick={closeMobileMenu} className="text-[#0057a3] hover:text-blue-800 font-medium">
                            Sign In
                          </Link>
                          <Link to="/signup" onClick={closeMobileMenu} className="bg-[#0057a3] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#01417f] transition-colors">
                            Sign Up
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isAuthenticated && (
                    <div className="bg-primary text-white p-4 flex justify-center">
                      <Link to="/signup" onClick={closeMobileMenu} className="w-full max-w-xs bg-[#0057a3] text-white border-2 border-[#0057a3] px-6 py-3 rounded-lg font-bold text-center hover:bg-[#0057a3] hover:text-white transition-colors">
                        Juror Reschedule
                      </Link>
                    </div>
                  )}
                  END OLD CODE */}

                  {/* ---- LOGGED IN: My Account entry untouched ---- */}
                  {isAuthenticated ? (
                    <div className="bg-white border-b border-gray-200">
                      <button
                        onClick={toggleMyAccount}
                        className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <UserCircleIcon className="h-8 w-8 text-gray-400" />
                          <div>
                            <div className="font-bold text-gray-900">My Account</div>
                            <div className="text-sm text-gray-600">{user?.name}</div>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                  ) : (
                    /* ---- NOT LOGGED IN: NEW My Account entry ---- */
                    /* NEW */
                    <div className="bg-white border-b border-gray-200">
                      <button
                        onClick={toggleMyAccount}
                        className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <UserCircleIcon className="h-8 w-8 text-gray-400" />
                          <div>
                            <div className="font-bold text-gray-900">My Account</div>
                            <div className="text-sm text-gray-600">Account & Login</div>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                    /* END NEW */
                  )}


                 {/* END NEW */}

                {/* Juror Reschedule Button - Only visible when NOT logged in */}
                {/* OLD CODE - unchanged, kept as is */}
                {!isAuthenticated && (
                  <div className="bg-primary text-white p-4 flex justify-center">
                    <Link
                      to="/signup"
                      onClick={closeMobileMenu}
                      className="w-full max-w-xs bg-[#0057a3] text-white border-2 border-[#0057a3] px-6 py-3 rounded-lg font-bold text-center hover:bg-[#0057a3] hover:text-white transition-colors"
                    >
                      Juror Reschedule
                    </Link>
                  </div>
                )}
                </div>
              </div>

              {/* My Account Submenu - Slides in from right */}
              {isAuthenticated ? (
                /* ---- LOGGED IN: submenu untouched ---- */
                <div className={`absolute inset-0 bg-white transition-transform duration-300 ${myAccountOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                  <div className="flex items-center p-4 border-b border-gray-200 bg-white">
                    <button onClick={toggleMyAccount} className="mr-3 text-gray-500 hover:text-gray-700">
                      <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-bold text-gray-900">My Account</h2>
                  </div>

                  <div className="mt-2">
                    <div className="bg-white border-b border-gray-200">
                      <button
                        onClick={() => handleMyAccountNavigation('/dashboard')}
                        className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <HomeIcon className="h-6 w-6 text-[#0057a3]" />
                          <div>
                            <div className="font-bold text-gray-900">Dashboard</div>
                            <div className="text-sm text-gray-600">View your dashboard</div>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>

                    <div className="bg-white border-b border-gray-200">
                      <button
                        onClick={() => handleMyAccountNavigation('/online-payment')}
                        className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6 text-[#0057a3]">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
                          </svg>
                          <div>
                            <div className="font-bold text-gray-900">Online Payment Center</div>
                            <div className="text-sm text-gray-600">Make payments online</div>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>

                    <div className="bg-white border-b border-gray-200">
                      <button
                        onClick={() => { handleLogout(); closeMobileMenu(); }}
                        className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <ArrowRightOnRectangleIcon className="h-6 w-6 text-red-600" />
                          <div>
                            <div className="font-bold text-red-600">Logout</div>
                            <div className="text-sm text-gray-600">Sign out of your account</div>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* ---- NOT LOGGED IN: NEW submenu with Account Summary, Online Payment Center, Login ---- */
                /* NEW */
                <div className={`absolute inset-0 bg-white transition-transform duration-300 ${myAccountOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                  <div className="flex items-center p-4 border-b border-gray-200 bg-white">
                    <button onClick={toggleMyAccount} className="mr-3 text-gray-500 hover:text-gray-700">
                      <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-bold text-gray-900">My Account</h2>
                  </div>

                  <div className="mt-2">
                    {/* Account Summary → Case Lookup page */}
                    <div className="bg-white border-b border-gray-200">
                      <button
                        onClick={() => handleMyAccountNavigation('/case-lookup')}
                        className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <HomeIcon className="h-6 w-6 text-[#0057a3]" />
                          <div>
                            <div className="font-bold text-gray-900">Account Summary</div>
                            <div className="text-sm text-gray-600">View your account summary</div>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>

                    {/* Account Summary → FDIC */}
                    <div className="bg-white border-b border-gray-200">
                      <button
                        onClick={() => handleMyAccountNavigation('/fintech')}
                        className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <HomeIcon className="h-6 w-6 text-[#0057a3]" />
                          <div>
                            <div className="font-bold text-gray-900">FDIC</div>
                            <div className="text-sm text-gray-600">Manage your FDIC account</div>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>

                    {/* Online Payment Center */}
                    <div className="bg-white border-b border-gray-200">
                      <button
                        onClick={() => handleMyAccountNavigation('/online-payment')}
                        className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6 text-[#0057a3]">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
                          </svg>
                          <div>
                            <div className="font-bold text-gray-900">Online Payment Center</div>
                            <div className="text-sm text-gray-600">Make payments online</div>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>

                    {/* Login */}
                    <div className="bg-white border-b border-gray-200">
                      <button
                        onClick={() => handleMyAccountNavigation('/login')}
                        className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <ArrowRightOnRectangleIcon className="h-6 w-6 text-[#0057a3]" />
                          <div>
                            <div className="font-bold text-gray-900">Login</div>
                            <div className="text-sm text-gray-600">Sign in to your account</div>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>
                /* END NEW */
              )}

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;